import { withStyles } from '@material-ui/core/styles';
import gql from 'graphql-tag';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { CreditCard, Payline, PaymentProvider, Primaccess, helpers } from '@aps-management/primapp-common';

import Payment from '_components/Payment';
import { Screen } from '_components/core';
import { DialogPaymentIP, PaylineWidgetFrame } from '_components/elements';
import DialogWallet3DS from '_components/elements/DialogWallet3DS';
import apolloClient from '_utils/apolloClient';
import captureSubscriberWithIntent from '_utils/captureSubscriberWithIntent';
import { assertWidgetPaymentSuccess } from '_utils/paymentGuard';

/* */
import { PAYLINE_FLOW_TYPES, setupPaylineListeners } from '../../../services/payline';

/* */
const styles = {};

/**
 * Annulation du débit lorsque le crédit des seaux a été explicitement rejeté par le
 * serveur. Déclarée localement, comme dans primapp-native : la mutation n'existe pas
 * dans les requêtes figées de `primapp-common`.
 */
const CANCEL_WALLET_PAYMENT = gql`
  mutation PaylineCancelWalletPayment($transactionId: String!, $amount: Int!, $reference: String!) {
    paylineCancelWalletPayment(transactionId: $transactionId, amount: $amount, reference: $reference) {
      success
      message
    }
  }
`;

/* */
class PrimaccessPayment extends React.Component {
  /* */
  constructor(props) {
    super(props);

    const { cart, golf, account } = this.props;

    this.redirect = false;
    if (cart.length === 0) {
      this.redirect = true;
    }

    const uuid = helpers.uuid.createUuid();
    const shortId = uuid.split('-')[0];

    this.data = {
      reference: `PA-${shortId}`,
      amount: cart.reduce((tot, product) => tot + product.price, 0),
      walletUuid: helpers.uuid.createUuid(),
    };

    // build session 3DS
    this.session3DS = {
      address: {
        city: golf.city,
        countryCode: 250, // golf.country
        line1: golf.name,
        zipCode: golf.postalCode,
      },
      customer: {
        email: account.email,
        lastName: account.lastname,
        firstName: account.firstname,
        phone: account.phone,
      },
    };
    this.currency = golf.currency;

    this.state = {
      // Screen
      error: null,
      loading: false,
      // Payment
      status: null,
      imprint: null,
      waiting: false,
      paylineToken: null,
      // Wallet 3DS flow
      wallet3DS: {
        open: false,
        threeDSData: null,
        subscriber: null,
        amount: null,
        reference: null,
      },
    };

    this.paylineWidgetData = null;
    this.isPaylinePaymentHandled = false;
    this.removePaylineListeners = null;
    // Passe à true dès que les seaux sont crédités : verrou contre toute annulation
    // déclenchée par une erreur survenant APRÈS la livraison.
    this.operationConfirmed = false;
    /*
     * Passe à true dès qu'un encaissement a RÉUSSI, avant même la livraison.
     * Verrou contre un second paiement pour le même panier.
     *
     * Le cas qu'il ferme (Mont de Marsan, 03/08/2026) : le paiement widget réussit,
     * la carte est enregistrée puis posée en `imprint`, et la confirmation échoue.
     * L'écran affichait alors une erreur, remettait `waiting` à false, et laissait
     * le panneau « payer en 1 clic » actif avec la carte fraîchement enregistrée.
     * Le golfeur a cliqué : 42 € prélevés pour un achat de 21 €.
     *
     * Le message d'erreur disait déjà « Ne renouvelez pas l'opération ». Ce n'est
     * pas une consigne qu'il faut donner au golfeur, c'est un bouton qu'il ne faut
     * plus lui présenter.
     */
    this.paymentCaptured = false;
  }

  /**
   * Vrai si l'argent a déjà été pris pour ce panier. Tout déclencheur de paiement
   * doit sortir sur ce test, sans exception : c'est le dernier rempart côté écran,
   * le premier étant le marqueur de capture côté master.
   */
  refuseIfAlreadyCaptured() {
    if (!this.paymentCaptured) return false;

    this.setState({
      waiting: false,
      status: null,
      error:
        'Votre paiement a déjà été accepté. Ne payez pas une seconde fois : '
        + 'le golf vérifie votre solde et vous recontacte si nécessaire.',
    });
    return true;
  }

  componentDidMount() {
    // Charger les cartes sauvegardées (sans accountId = résolu via Firebase par primapp-api)
    CreditCard.api
      .getCreditCards(apolloClient)
      .then(({ creditCards }) => {
        // Garde-fou : seule une carte disposant d'un wallet Payline réel (walletId) peut
        // être débitée en 1 clic. Une carte sans walletId (enregistrée avant le correctif
        // ou via un chemin legacy) produirait subscriber.id=null → mutation rejetée. On ne
        // la propose donc pas : l'utilisateur ressaisit sa carte (le widget crée un vrai
        // wallet), ce qui répare la situation de lui-même.
        const usable = (creditCards || []).find((c) => c && c.walletId);
        if (usable) {
          this.setState({ imprint: usable });
        }
      })
      .catch(() => {});
  }

  /*
   * `setupPaylineListeners` pose ses écouteurs sur `window`, pas sur le composant :
   * sans cette libération, ils survivent au démontage de l'écran. Un aller-retour
   * laisse alors deux instances à l'écoute du même widget, et le succès du paiement
   * déclenche DEUX fois la chaîne de confirmation - donc deux crédits de seaux pour
   * un seul débit, chacune avec son propre garde-fou d'instance qui ne voit rien.
   * Cf. incident Golf aux Daims du 25/07/2026.
   */
  componentWillUnmount() {
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }
  }

  /**
   * Ce qu'il faudra livrer une fois l'argent pris, déclaré AVANT de payer.
   *
   * Sans cela, un paiement dont le navigateur ne revient jamais est irrattrapable
   * côté serveur : la session de paiement sait qui paie, sur quel golf et
   * combien, mais pas QUOI. Le produit n'arrive qu'avec `createOperation`,
   * c'est-à-dire dans l'appel qui peut ne jamais partir. C'est exactement le cas
   * du 03/08 à Mont de Marsan, où le golfeur n'a eu ses seaux qu'en repayant.
   *
   * Construit à partir des MÊMES sources que `creditWallet`, pour que l'intention
   * et la livraison ne puissent pas décrire deux choses différentes.
   */
  buildDeliveryIntent() {
    /*
     * NE JETTE JAMAIS, et le contrôle porte sur `golf.id` et non sur `golf`.
     *
     * Une intention est un confort de reprise, jamais une raison d'empêcher un
     * golfeur de payer. Ce constructeur est appelé de façon synchrone AVANT la
     * mutation : une exception - `filterByKeys` sur un panier malformé, un golf
     * sans identifiant - remonterait avant tout encaissement et ferait échouer
     * le paiement. Aligné sur `primapp-native/src/utils/deliveryIntent.js`.
     */
    try {
      const { clientId, cart, golf } = this.props;
      if (!clientId || !cart || !cart.length || !golf || !golf.id) return undefined;

      return JSON.stringify({
        scope: 'PA_createOperation',
        clientId,
        golfId: golf.id,
        product: helpers.object.filterByKeys(cart[0], ['id', 'name', 'value', 'price']),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[payment] Primaccess.buildDeliveryIntent.failed', err);
      return undefined;
    }
  }

  /**
   * Crédite les seaux, sauf si le serveur l'a déjà fait.
   *
   * @param {boolean} [dejaLivre] Le serveur a livré dans la foulée de la capture
   */
  creditWallet(dejaLivre = false) {
    const { transaction } = this.data;
    const { clientId, cart, golf } = this.props;

    this.props.update({ transaction });

    /*
     * LE SERVEUR A DÉJÀ LIVRÉ : ne pas rappeler.
     *
     * Le crédit appartenait à ce SECOND appel, émis par le navigateur après la
     * capture ; tout ce qui coupait entre les deux - onglet fermé, réseau perdu,
     * attente qui fait renoncer - prenait l'argent sans rien livrer.
     *
     * `operationConfirmed` est posé comme après une livraison réussie : la
     * marchandise EST partie, plus rien en aval ne doit annuler ce paiement.
     */
    if (dejaLivre) {
      this.operationConfirmed = true;
      return Promise.resolve(this.goToVoucher());
    }

    /*
     * Le serveur n'a pas livré : comportement d'avant ce lot, à l'identique,
     * gestion d'erreur comprise.
     */
    /*
     * `filterByKeys` fait `Object.keys(data)` et JETTE donc sur un panier vide,
     * cas que le contrôle `!cart.length` de `buildDeliveryIntent` ne couvre pas.
     * Ici on est APRÈS le débit : une exception nue partirait dans le `.catch`
     * générique de l'appelant, qui se contente d'afficher un message - sans
     * `handleOperationFailure`, donc sans trace exploitable et sans le
     * raisonnement d'annulation. On la route explicitement.
     *
     * Ce chemin ne perd pas l'argent pour autant : l'intention a été jointe à la
     * capture, donc la session porte de quoi livrer et le cron de réconciliation
     * reprend le travail. Et `handleOperationFailure` n'annulera pas, puisqu'une
     * erreur locale n'est pas un rejet explicite du serveur.
     */
    let product;
    try {
      product = helpers.object.filterByKeys(cart[0], ['id', 'name', 'value', 'price']);
    } catch (err) {
      return this.handleOperationFailure(err);
    }

    return Primaccess.api
      .createOperation(apolloClient, {
        clientId,
        transaction,
        golfId: golf.id,
        product,
      })
      .then(() => {
        // Les seaux sont crédités : plus rien en aval ne doit annuler le paiement.
        this.operationConfirmed = true;
        return this.goToVoucher();
      })
      .catch((err) => this.handleOperationFailure(err));
  }

  /**
   * Échec d'une étape POSTÉRIEURE au débit : le porteur est débité mais son solde
   * de seaux n'est peut-être pas crédité. Portage de la logique éprouvée du natif
   * (`primapp-native/src/screens/Primaccess/Cart/Payment.js`).
   *
   * On n'annule que si le serveur a explicitement rejeté l'opération, ET que son
   * verdict ne dit pas l'inverse (voir `VERDICTS_SANS_ANNULATION` plus bas). Une
   * erreur réseau (coupure, timeout, réponse illisible) ne dit RIEN du sort de la
   * requête : `Primaccess.purchase` a pu créer la commande, créditer les seaux et
   * émettre le ticket PGB avant que la réponse ne se perde. Annuler dans ce cas
   * rembourse un golfeur pourtant servi.
   *
   * Le rejeu reste fermé ici : sur les golfs où l'idempotence de livraison n'est pas
   * allumée, chaque appel crée une nouvelle commande côté serveur. Sur une issue
   * inconnue, le seul comportement sûr est donc de NE RIEN FAIRE de plus, de le dire
   * clairement au golfeur, et de laisser une trace console exploitable par le
   * support. Le rattrapage, lui, se fait côté serveur.
   */
  handleOperationFailure = async (err) => {
    if (this.operationConfirmed) {
      // eslint-disable-next-line no-console
      console.error('[payment] Primaccess.afterOperationConfirmed.error', err);
      return;
    }

    const isAfterCapture = !!this.data.transaction?.transactionNo;

    if (!isAfterCapture) {
      this.setState({ error: err.message, waiting: false, status: null });
      return;
    }

    // Verdict serveur explicite = l'opération n'a pas été créée : annuler est
    // correct, SAUF pour les quatre verdicts listés ci-dessous.
    // Absence de verdict (networkError, timeout) = issue inconnue : ne pas annuler.
    const ATTENTE =
      'Votre paiement est enregistré et sa validation est en cours. '
      + 'Ne payez pas une seconde fois : patientez quelques instants, puis rouvrez votre solde.';
    const NON_CONFIRME =
      'Votre paiement a été accepté mais nous n’avons pas pu confirmer le rechargement. '
      + 'Ne renouvelez pas l’opération : le golf vérifie votre solde et vous recontacte.';
    /*
     * Doublon et livraison en cours ne sont PAS le même fait, et le message le
     * dit. `duplicate_in_progress` vient du verrou anti-doublon de master
     * (`paymentTracing.js`), qui n'est délibérément pas libéré en cas d'échec :
     * on le reçoit donc aussi quand le premier appel a échoué sans rien livrer.
     * Promettre « la validation est en cours » serait faux dans ce cas. Le texte
     * repris ici est celui de `primapp-common/src/translations/fr/errors.js`,
     * pour que la même clé serveur ne se lise pas de deux façons différentes.
     */
    const DOUBLON =
      'Une opération identique est déjà en cours d’enregistrement. '
      + 'Ne renouvelez pas le paiement : vérifiez votre solde, et contactez votre golf en cas de doute.';
    /*
     * Ne PAS écrire « vous pouvez réessayer » : à ce stade `paymentCaptured` vaut
     * déjà true, et tout nouveau clic sur PAYER se fait renvoyer « Votre paiement
     * a déjà été accepté ». Le verrou est correct et doit rester ; c'est la
     * phrase qui doit dire où aller.
     */
    const RENDU =
      'Le paiement a échoué et a été annulé. Aucun montant ne restera débité. '
      + 'Revenez à votre solde pour relancer le rechargement.';

    /*
     * Verdicts serveur qui interdisent d'annuler, malgré leur forme d'erreur, et
     * ce qu'il faut dire au golfeur pour chacun.
     *
     * `serverRejected` suppose qu'une erreur GraphQL signifie « l'opération n'a
     * pas été créée, donc rembourser est correct ». Quatre verdicts disent
     * autre chose, et deux d'entre eux appellent un message OPPOSÉ :
     *
     *  - `delivery_in_progress` : une livraison identique est EN COURS côté
     *    serveur, lancée par le golfeur lui-même ou par le job de
     *    réconciliation. Annuler rembourserait une marchandise en train d'être
     *    livrée.
     *  - `duplicate_in_progress` : un appel identique est déjà passé dans les
     *    30 dernières secondes. Cela ne prouve PAS qu'une livraison est en
     *    cours, seulement qu'il ne faut pas en lancer une seconde, et que le
     *    premier appel a géré son propre sort.
     *  - `compensation_failed` : le crédit a DÉJÀ été demandé au golf, la
     *    marchandise est partie. Annuler l'offrirait.
     *  - `reversed` : le paiement a été RENDU. Il n'y a rien à annuler, et
     *    surtout il ne faut pas dire au golfeur de patienter : rien ne viendra.
     *
     * Le cas « remboursement automatique en échec » porte son propre message
     * serveur (`refund_failed`) et n'est délibérément PAS dans cette liste :
     * c'est le seul où une nouvelle tentative d'annulation sert encore.
     */
    const VERDICTS_SANS_ANNULATION = {
      'errors.payment.delivery_in_progress': ATTENTE,
      'errors.payment.duplicate_in_progress': DOUBLON,
      'errors.payment.compensation_failed': NON_CONFIRME,
      'errors.payment.reversed': RENDU,
    };

    /*
     * On lit `graphQLErrors[].message`, qui porte le message brut du serveur.
     * Le repli sur `err.message` utilise `includes` et non une égalité : Apollo
     * y préfixe « GraphQL error: », si bien qu'une comparaison stricte n'aurait
     * JAMAIS été vraie et aurait donné une fausse assurance.
     */
    const verdict = (err?.graphQLErrors || [])
      .map((e) => e?.message)
      .find((m) => VERDICTS_SANS_ANNULATION[m])
      || Object.keys(VERDICTS_SANS_ANNULATION)
        .find((m) => String(err?.message || '').includes(m));

    const serverRejected = Array.isArray(err?.graphQLErrors)
      && err.graphQLErrors.length > 0
      && !verdict;

    if (!serverRejected) {
      // eslint-disable-next-line no-console
      // Deux marqueurs distincts : un verdict serveur reconnu n'est pas une issue
      // inconnue, et les confondre rend le grep de production inexploitable.
      console.error(`[payment] Primaccess.createOperation.${verdict ? 'verdict_no_cancel' : 'inconclusive_no_cancel'}`, {
        transactionNo: this.data.transaction.transactionNo,
        reference: this.data.reference,
        amount: this.data.amount,
        error: err.message,
        verdict: verdict || null,
        networkError: !!err?.networkError,
      });

      this.setState({
        error:
          verdict ? VERDICTS_SANS_ANNULATION[verdict] : NON_CONFIRME,
        waiting: false,
        status: null,
      });
      return;
    }

    // eslint-disable-next-line no-console
    console.error('[payment] Primaccess.createOperation.failed_attempting_cancel', {
      transactionNo: this.data.transaction.transactionNo,
      error: err.message,
    });

    this.setState({ status: 'Annulation du paiement en cours...' });

    try {
      const cancelResult = await apolloClient.mutate({
        mutation: CANCEL_WALLET_PAYMENT,
        variables: {
          transactionId: this.data.transaction.transactionNo,
          amount: this.data.amount,
          reference: this.data.reference,
        },
      });

      const cancelled = cancelResult.data?.paylineCancelWalletPayment?.success;

      this.setState({
        error: cancelled
          ? 'Le rechargement a échoué et votre paiement a bien été annulé. Veuillez réessayer ou contacter le golf.'
          : 'Le rechargement a échoué. Le golf a été notifié. Vérifiez auprès de votre banque que vous n’avez pas été débité.',
        waiting: false,
        status: null,
      });
    } catch (cancelErr) {
      // eslint-disable-next-line no-console
      console.error('[payment] Primaccess.createOperation.cancelError', cancelErr);
      this.setState({
        error: 'Le rechargement a échoué. Veuillez contacter le golf pour vérifier votre paiement.',
        waiting: false,
        status: null,
      });
    }
  };

  /* */
  goToVoucher = () => this.props.history.push('/primaccess/voucher');

  /* */
  capture(creditCard) {
    const { account } = this.props;
    const { id3D = null } = creditCard;
    const { amount, reference } = this.data;

    if (creditCard.saveImprint) {
      const uuid = helpers.uuid.createUuid();

      return Payline.api
        .subscribeAndCapture(apolloClient, {
          uuid,
          id3D,
          amount,
          reference: account.id,
          creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
        })
        .catch((err) => {
          throw Object.assign(err, { handled: true });
        })
        .then((res) =>
          CreditCard.api
            .setCreditCard(apolloClient, {
              id: uuid,
              token: res.token,
              number: CreditCard.functions.anonymizeNumber(creditCard.number),
              ...helpers.object.filterByKeys(creditCard, ['dueDate', 'country', 'brand', 'type']),
            })
            .then(() => res),
        )
        .catch((err) => {
          if (!err.handled) {
            throw Object.assign(err, {
              handled: true,
              message: 'Impossible d’enregistrer la carte. Veuillez réessayer ultérieurement.',
            });
          }
          throw err;
        });
    }

    return Payline.api.capture(apolloClient, {
      id3D,
      amount,
      reference,
      creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
    });
  }

  /* */
  payWithNewCard = (values, { setSubmitting }) => {
    if (this.refuseIfAlreadyCaptured()) {
      if (setSubmitting) setSubmitting(false);
      return Promise.resolve();
    }

    const creditCard = {
      ...values,
      name: '3DS web',
      number: values.number.replace(/\s/g, ''),
      dueDate: values.dueDate.replace(/\D/g, ''),
    };

    this.setState({
      error: null,
      waiting: true,
      status: 'Nous interrogeons votre banque...',
    });

    return this.capture(creditCard)
      .catch((err) => {
        if (!err.handled) {
          this.setState({
            status: 'Erreur lors de la transaction, annulation en cours...',
          });
        }
        throw err;
      })
      .then((res) => {
        this.paymentCaptured = true;
        this.data.transaction = {
          currency: this.currency,
          callNo: res.authorizationNumber || res.NUMAPPEL,
          amount: this.data.amount,
          transactionNo: res.transactionId || res.NUMTRANS,
          card: CreditCard.functions.anonymizeNumber(creditCard.number),
          mode: 0,
          ...helpers.object.filterByKeys(creditCard, ['type', 'brand', 'country']),
        };

        return this.creditWallet();
      })
      .catch((err) => {
        this.setState({ error: err.message, waiting: false });
        if (setSubmitting) setSubmitting(false);
      });
  };

  /* */
  payWithCardImprint = () => {
    if (this.refuseIfAlreadyCaptured()) return Promise.resolve();

    const { imprint } = this.state;

    // Garde-fou (défense en profondeur) : ne jamais tenter un paiement 1-clic avec une
    // carte dépourvue de wallet Payline réel. Normalement déjà filtré au chargement.
    if (!imprint || !imprint.walletId) {
      this.setState({
        error:
          "Cette carte enregistrée n'est plus utilisable pour le paiement en 1 clic. Réglez avec une nouvelle carte : elle sera ré-enregistrée automatiquement.",
      });
      return Promise.resolve();
    }

    const subscriber = {
      id: imprint.walletId,
      token: imprint.token,
      dueDate: imprint.dueDate,
    };

    this.setState({
      error: null,
      waiting: true,
      status: 'Nous interrogeons votre banque...',
    });

    /*
     * `captureSubscriberWithIntent` et non `Payline.api.captureSubscriber` : le
     * document du paquet commun ne déclare ni la variable `deliveryIntent` ni le
     * champ `delivery` de la réponse, et ce qui n'est pas déclaré n'existe pas
     * pour le serveur. Le motif complet est dans
     * `_utils/captureSubscriberWithIntent`.
     */
    const captureApi = captureSubscriberWithIntent;

    /*
     * L'intention voyage désormais AUSSI par le chemin en un clic - elle ne
     * partait jusqu'ici que par le widget. C'est elle qui arme tout le reste :
     * sans elle le serveur n'écrit aucune session, ne peut donc rien livrer de
     * lui-même, et le crédit resterait suspendu à un second appel de ce
     * navigateur.
     */
    return captureApi(apolloClient, {
      ...this.data,
      subscriber,
      deliveryIntent: this.buildDeliveryIntent(),
    })
      .catch((err) => {
        if (!err.handled) {
          this.setState({
            status: 'Erreur lors de la transaction, annulation en cours...',
          });
        }
        throw Object.assign(err, { handled: true });
      })
      .then((res) => {
        // Handle 3DS challenge required for wallet payments
        if (res && res.needs3DS) {
          if (res.threeDSData?.actionUrl) {
            // ACS redirect URL present: show the 3DS challenge dialog
            this.setState({
              waiting: false,
              status: null,
              wallet3DS: {
                open: true,
                threeDSData: res.threeDSData,
                subscriber,
                amount: this.data.amount,
                reference: this.data.reference,
              },
            });
            const cancelErr = new Error('3DS_CHALLENGE_REQUIRED');
            cancelErr.is3DS = true;
            throw cancelErr;
          }
          // No ACS URL: fall back to Payline hosted widget with saved card pre-selected
          this.setState({ status: 'Chargement du formulaire de paiement...' });
          return Payline.api.getToken(apolloClient, {
            amount: this.data.amount,
            reference: this.data.reference,
            currency: this.currency,
            walletId: subscriber.id,
            returnUrl: `${window.location.origin}/primaccess/voucher`,
            cancelUrl: `${window.location.origin}/primaccess/payment`,
            callbackUrl: `${window.location.origin}/payline/callback`,
            customer: {
              firstName: this.props.account.firstname,
              lastName: this.props.account.lastname,
              email: this.props.account.email,
            },
            // Repli du portefeuille vers le widget : c'est le même achat, il
            // doit donc déclarer la même intention que l'autre ouverture de
            // session, sinon ce parcours-là resterait irrattrapable.
            deliveryIntent: this.buildDeliveryIntent(),
          }).then((tokenRes) => {
            if (!tokenRes?.token) throw new Error('Token de paiement introuvable.');
            this.paylineWidgetData = { token: tokenRes.token };
            this.isPaylinePaymentHandled = false;
            if (this.removePaylineListeners) {
              this.removePaylineListeners();
              this.removePaylineListeners = null;
            }
            this.removePaylineListeners = setupPaylineListeners(PAYLINE_FLOW_TYPES.CHECKOUT, {
              onSuccess: (eventData) => this.handlePaylinePaymentSuccess(eventData),
              onFailure: (eventData) => this.handlePaylinePaymentError(eventData, 'Le paiement a échoué. Veuillez réessayer.'),
              onCancel: (eventData) => this.handlePaylinePaymentError(eventData, 'Le paiement a été annulé.'),
            });
            this.setState({ paylineToken: tokenRes.token, waiting: false, status: null });
            // Stop the original promise chain: the widget callback handles creditWallet.
            const stopErr = new Error('WIDGET_FALLBACK');
            stopErr.is3DS = true;
            throw stopErr;
          });
        }

        this.paymentCaptured = true;
        this.data.transaction = {
          currency: this.currency,
          card: imprint.number,
          callNo: res.authorizationNumber || res.NUMAPPEL,
          amount: this.data.amount,
          creditCardId: imprint.id,
          transactionNo: res.transactionId || res.NUMTRANS,
          mode: 0,
          ...helpers.object.filterByKeys(imprint, ['type', 'brand', 'country']),
        };

        /*
         * `=== true` et non une valeur vraie quelconque : ce champ est nul sur
         * tout serveur antérieur à ce lot, et un navigateur peut interroger un
         * serveur plus ancien que lui. Une lecture laxiste ferait sauter le
         * crédit devant un serveur qui n'a rien livré.
         */
        const livraison = res?.delivery;
        /*
         * Deux situations très différentes se ressemblent ici, et sans cette
         * trace elles sont indiscernables depuis le navigateur : le golf n'est
         * pas armé (le serveur s'abstient, tout est normal, le crédit suit), ou
         * la livraison a été REFUSÉE après un encaissement (l'argent est pris,
         * le crédit ci-dessous devient le seul filet). `status` et `scope` ne
         * servent qu'à ça - ne rien en déduire par programme, la liste des
         * verdicts appartient au serveur et s'allongera.
         */
        if (livraison && livraison.done !== true) {
          // eslint-disable-next-line no-console
          console.warn('[payment] Primaccess.delivery_not_done', {
            status: livraison.status,
            scope: livraison.scope,
            transactionNo: res.transactionId || res.NUMTRANS,
          });
        }

        return this.creditWallet(livraison?.done === true);
      })
      .catch((err) => {
        // 3DS challenge in progress - don't show error
        if (err && err.is3DS) {
          return;
        }
        this.setState({ error: err.message, waiting: false });
      });
  };

  handleWallet3DSClose = () => {
    this.setState({
      wallet3DS: {
        open: false,
        threeDSData: null,
        subscriber: null,
        amount: null,
        reference: null,
      },
      error: 'Authentification 3D Secure annul\u00e9e.',
    });
  };

  // Verrou de ré-entrée SYNCHRONE : un double message ACS (POST 3DS) ne doit pas rejouer le débit.
  _wallet3DSInFlight = false;

  handleWallet3DSComplete = ({ pares, md }) => {
    if (this._wallet3DSInFlight) return;
    if (this.refuseIfAlreadyCaptured()) return;
    this._wallet3DSInFlight = true;
    const { wallet3DS } = this.state;

    this.setState({
      wallet3DS: {
        open: false,
        threeDSData: null,
        subscriber: null,
        amount: null,
        reference: null,
      },
      waiting: true,
      status: 'Nous interrogeons votre banque...',
    });

    Payline.api
      .captureSubscriberAfter3DS(apolloClient, {
        amount: wallet3DS.amount,
        reference: wallet3DS.reference,
        walletId: wallet3DS.subscriber.id,
        pares,
        md,
      })
      .then((res) => {
        this.paymentCaptured = true;
        this.data.transaction = {
          currency: this.currency,
          card: this.state.imprint.number,
          callNo: res.authorizationNumber || res.NUMAPPEL,
          amount: this.data.amount,
          creditCardId: this.state.imprint.id,
          transactionNo: res.transactionId || res.NUMTRANS,
          mode: 0,
          ...helpers.object.filterByKeys(this.state.imprint, ['type', 'brand', 'country']),
        };

        return this.creditWallet();
      })
      .catch((err) => {
        this.setState({
          status: null,
          waiting: false,
          error: err.message || 'Le paiement a \u00e9chou\u00e9.',
        });
      })
      .finally(() => {
        this._wallet3DSInFlight = false;
      });
  };

  payWithNewCardPayline = () => {
    if (this.refuseIfAlreadyCaptured()) return Promise.resolve();

    const data = {};
    const saveImprint = sessionStorage.getItem('payline_saveImprint') === '1';

    this.setState({
      error: null,
      waiting: true,
      status: 'Nous préparons le paiement...',
    });

    return Payline.api
      .getToken(apolloClient, {
        amount: this.data.amount,
        reference: this.data.reference,
        currency: this.currency,
        returnUrl: `${window.location.origin}/primaccess/voucher`,
        cancelUrl: `${window.location.origin}/primaccess/payment`,
        callbackUrl: `${window.location.origin}/payline/callback`,
        customer: {
          firstName: this.props.account.firstname,
          lastName: this.props.account.lastname,
          email: this.props.account.email,
          phone: this.props.account.phone,
        },
        deliveryIntent: this.buildDeliveryIntent(),
      })
      .then((res) => {
        if (!res?.token) {
          throw new Error('Token de paiement introuvable.');
        }

        data.token = res.token;

        this.setState({
          paylineToken: data.token,
          waiting: false,
          status: null,
        });

        this.paylineWidgetData = data;
        this.isPaylinePaymentHandled = false;

        if (this.removePaylineListeners) {
          this.removePaylineListeners();
        }

        this.removePaylineListeners = setupPaylineListeners(PAYLINE_FLOW_TYPES.CHECKOUT, {
          onSuccess: (eventData) => this.handlePaylinePaymentSuccess(eventData),
          onFailure: (eventData) =>
            this.handlePaylinePaymentError(eventData, 'Le paiement a échoué. Veuillez réessayer.'),
          onCancel: (eventData) => this.handlePaylinePaymentError(eventData, 'Le paiement a été annulé.'),
        });
      })
      .catch((err) => {
        this.setState({
          error: err.message,
          waiting: false,
          status: null,
          paylineToken: null,
        });
      });
  };

  /* */
  handlePaylinePaymentSuccess = (eventData = {}) => {
    if (this.isPaylinePaymentHandled || !this.paylineWidgetData) {
      return;
    }

    // Le `catch` en fin de chaîne remet `isPaylinePaymentHandled` à false pour
    // autoriser une reprise après un échec ANTÉRIEUR à la capture. Une fois
    // l'argent pris, cette reprise ne doit plus repasser par `captureWithToken`.
    if (this.refuseIfAlreadyCaptured()) {
      return;
    }

    this.isPaylinePaymentHandled = true;

    const data = this.paylineWidgetData;
    const token = eventData?.token || data.token;
    const saveImprint = sessionStorage.getItem('payline_saveImprint') === '1';

    this.setState({ paylineToken: null });
    this.paylineWidgetData = null;
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }

    this.setState({
      error: null,
      waiting: true,
      status: 'Vérification du paiement...',
    });

    const processPayment = saveImprint
      ? Payline.api.captureWithToken(apolloClient, {
          token,
          amount: this.data.amount,
          uuid: this.data.walletUuid,
          reference: this.data.reference,
          customer: {
            firstName: this.props.account.firstname,
            lastName: this.props.account.lastname,
            email: this.props.account.email,
          },
        })
      : Payline.api.checkPayment(apolloClient, { token });

    return processPayment
      .then((result) => {
        // Le verdict du widget n'était pas lu : `paylineCheckPayment` renvoie un
        // paiement REFUSÉ en résolution normale (`status: 'failed'`), et l'écran
        // enchaînait sur le crédit des seaux. Cf. `_utils/paymentGuard`.
        assertWidgetPaymentSuccess(result, 'Primaccess.widgetPayment');

        // L'argent est pris. Tout ce qui suit (enregistrement de la carte, crédit
        // des seaux) peut échouer sans que cela redonne le droit de payer.
        this.paymentCaptured = true;

        const transaction = {
          amount: this.data.amount,
          currency: this.currency,
          provider: 'payline',
          mode: 0,
          callNo: result.authorizationNumber,
          transactionNo: result.transactionId,
          card: result.maskedCardNumber,
          brand: result.cardBrand,
          country: result.cardCountry,
        };

        if (saveImprint) {
          this.setState({ status: 'Enregistrement de la carte...' });
          return CreditCard.api
            .setCreditCard(apolloClient, {
              id: helpers.uuid.createUuid(),
              token: result.token || token,
              number: result.maskedCardNumber,
              name: 'Payline',
              dueDate: result.expiryDate ? result.expiryDate : result.expirationDate ? result.expirationDate : '0000',
              brand: result.cardBrand,
              country: result.cardCountry,
              walletId: result.walletId,
              paymentRecordId: result.transactionId,
            })
            .then(() => {
              sessionStorage.removeItem('payline_saveImprint');
              return CreditCard.api
                .getCreditCards(apolloClient)
                .then(({ creditCards }) => {
                  if (creditCards && creditCards.length > 0) {
                    this.setState({ imprint: creditCards[0] });
                  }
                })
                .catch(() => {});
            })
            .then(() => {
              this.setState({ status: 'Nous confirmons votre paiement...' });
              this.data.transaction = transaction;
              return this.creditWallet();
            });
        }

        sessionStorage.removeItem('payline_saveImprint');
        this.setState({
          status: 'Nous confirmons votre paiement...',
        });

        this.data.transaction = transaction;

        return this.creditWallet();
      })
      .catch((err) => {
        this.isPaylinePaymentHandled = false;
        this.setState({
          error: err.message,
          waiting: false,
          status: null,
        });
      });
  };

  handlePaylinePaymentError = (eventData = {}, defaultMessage = 'Le paiement a échoué. Veuillez réessayer.') => {
    this.setState({ paylineToken: null });
    this.paylineWidgetData = null;
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }

    this.setState({
      waiting: false,
      status: null,
      error: eventData?.message || defaultMessage,
    });

    this.isPaylinePaymentHandled = false;
  };

  /* */
  render() {
    const { error, status, imprint, loading, waiting } = this.state;
    const { amount } = this.data;

    if (this.redirect) {
      return <Redirect to='/primaccess' />;
    }

    return (
      <Screen error={error} loading={loading} title='Practice - Paiement par CB'>
        <Payment
          amount={amount}
          golf={this.props.golf}
          imprint={imprint}
          currency={this.currency}
          session3DS={this.session3DS}
          isPaylineProvider={PaymentProvider.isPayline(this.props.golf)}
          payWithNewCard={this.payWithNewCard}
          payWithNewCardPayline={this.payWithNewCardPayline}
          payWithCardImprint={this.payWithCardImprint}
        />
        {this.state.paylineToken && (
          <PaylineWidgetFrame
            token={this.state.paylineToken}
            options={{ flowType: PAYLINE_FLOW_TYPES.CHECKOUT, amount: amount }}
            title='Payline Widget'
          />
        )}
        <DialogPaymentIP open={waiting} status={status} />
        <DialogWallet3DS
          open={this.state.wallet3DS.open}
          threeDSData={this.state.wallet3DS.threeDSData}
          onClose={this.handleWallet3DSClose}
          onComplete={this.handleWallet3DSComplete}
        />
      </Screen>
    );
  }
}

const mapStateToProps = ({ app: { account, golf }, primaccess: { cart, clientId } }) => ({
  cart,
  golf,
  account,
  clientId,
});

const StyledComponent = withStyles(styles)(PrimaccessPayment);

export default connect(mapStateToProps, Primaccess.actions)(StyledComponent);
